import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  HiOutlineCalendarDays,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlinePlus,
  HiOutlineArrowRight,
} from "react-icons/hi2";
import { db } from "../../db/firebase";
import { useAuth } from "../../context/AuthContext";
import { AnimatedModal, modalPanelClass } from "../../components/AnimatedModal";
import {
  TimeOffRequest,
  TimeOffStatus,
  createTimeOffRequest,
  fetchOrgTimeOffRequests,
  isDateInTimeOffRange,
  updateTimeOffRequestStatus,
} from "../../utils/timeOff";

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time?: string;
  status?: string;
}

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60";

const glassBtnMuted =
  "inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-60";

const statusStyles: Record<TimeOffStatus, string> = {
  pending:
    "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
  approved:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-600 dark:text-red-300 border-red-500/20",
  cancelled:
    "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20",
};

const CalendarPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAdmin, organizationId, profile } = useAuth();
  const [monthCursor, setMonthCursor] = useState(() =>
    moment().startOf("month"),
  );
  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(() =>
    moment().format("YYYY-MM-DD"),
  );
  const [requestOpen, setRequestOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  const displayName = useMemo(
    () =>
      [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() ||
      profile?.email ||
      t("unknownUser") ||
      "User",
    [profile, t],
  );

  const loadCalendarData = useCallback(async () => {
    if (!organizationId) {
      setRequests([]);
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [rows, eventsSnap] = await Promise.all([
        fetchOrgTimeOffRequests(organizationId),
        getDocs(
          query(
            collection(db, "events"),
            where("organizationId", "==", organizationId),
            where("status", "==", "Active"),
          ),
        ),
      ]);

      rows.sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
      setRequests(rows);

      const eventRows: CalendarEvent[] = eventsSnap.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title || t("untitledEvent") || "Untitled event",
            date: data.date || "",
            time: data.time || "",
            status: data.status || "Active",
          };
        })
        .filter((e) => e.date)
        .sort((a, b) => {
          const byDate = a.date.localeCompare(b.date);
          if (byDate !== 0) return byDate;
          return (a.time || "").localeCompare(b.time || "");
        });
      setEvents(eventRows);
    } catch (err) {
      console.error(err);
      setError(t("calendarLoadError") || "Could not load calendar.");
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const myRequests = useMemo(
    () => requests.filter((r) => r.userId === user?.uid),
    [requests, user?.uid],
  );

  /** Pending: only the requester sees their own; admins see everyone's. */
  const pendingForAdmin = useMemo(
    () => requests.filter((r) => r.status === "pending"),
    [requests],
  );

  const myPending = useMemo(
    () => myRequests.filter((r) => r.status === "pending"),
    [myRequests],
  );

  /**
   * Time off shown on the calendar / day panel:
   * - Member: only their own pending + approved
   * - Admin: everyone's approved + everyone's pending
   */
  const visibleTimeOff = useMemo(() => {
    if (isAdmin) {
      return requests.filter(
        (r) => r.status === "approved" || r.status === "pending",
      );
    }
    return myRequests.filter(
      (r) => r.status === "approved" || r.status === "pending",
    );
  }, [isAdmin, requests, myRequests]);

  const listRequests = useMemo(() => {
    if (isAdmin) {
      // Admin list excludes pending here — those live in the Pending panel
      return requests.filter(
        (r) => r.status === "approved" || r.status === "rejected",
      );
    }
    return myRequests.filter((r) => r.status !== "cancelled");
  }, [isAdmin, requests, myRequests]);

  const calendarDays = useMemo(() => {
    const start = monthCursor.clone().startOf("month").startOf("week");
    const end = monthCursor.clone().endOf("month").endOf("week");
    const days: moment.Moment[] = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day")) {
      days.push(cursor.clone());
      cursor.add(1, "day");
    }
    return days;
  }, [monthCursor]);

  const requestsForDate = (dateStr: string) =>
    visibleTimeOff.filter((r) =>
      isDateInTimeOffRange(dateStr, r.startDate, r.endDate),
    );

  const eventsForDate = (dateStr: string) =>
    events.filter((e) => e.date === dateStr);

  const eventsInMonth = useMemo(() => {
    const prefix = monthCursor.format("YYYY-MM");
    return events.filter((e) => e.date.startsWith(prefix));
  }, [events, monthCursor]);

  const selectedDayEvents = useMemo(
    () => eventsForDate(selectedDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, selectedDate],
  );

  const selectedDayRequests = useMemo(
    () => requestsForDate(selectedDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTimeOff, selectedDate],
  );

  const openRequestForDay = (dateStr: string) => {
    setStartDate(dateStr);
    setEndDate(dateStr);
    setReason("");
    setRequestOpen(true);
  };

  const selectDay = (dateStr: string) => {
    setSelectedDate(dateStr);
  };

  const eventLink = (eventId: string) =>
    isAdmin ? `/events/${eventId}` : `/my-events/${eventId}`;

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !organizationId || !startDate || !endDate) return;
    if (endDate < startDate) {
      setError(t("timeOffInvalidRange") || "End date must be on or after start date.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await createTimeOffRequest({
        organizationId,
        userId: user.uid,
        userName: displayName,
        startDate,
        endDate,
        reason: reason.trim(),
        requestId: uuidv4(),
      });
      setRequestOpen(false);
      setReason("");
      await loadCalendarData();
    } catch (err) {
      console.error(err);
      setError(t("timeOffSubmitError") || "Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  const reviewRequest = async (
    request: TimeOffRequest,
    status: "approved" | "rejected",
  ) => {
    if (!user) return;
    setActionId(request.id);
    try {
      await updateTimeOffRequestStatus({
        userId: request.userId,
        requestId: request.id,
        status,
        reviewedBy: user.uid,
      });
      await loadCalendarData();
    } catch (err) {
      console.error(err);
      setError(t("timeOffReviewError") || "Could not update request.");
    } finally {
      setActionId(null);
    }
  };

  const cancelRequest = async (request: TimeOffRequest) => {
    setActionId(request.id);
    try {
      await updateTimeOffRequestStatus({
        userId: request.userId,
        requestId: request.id,
        status: "cancelled",
        reviewedBy: request.userId,
      });
      await loadCalendarData();
    } catch (err) {
      console.error(err);
      setError(t("timeOffCancelError") || "Could not cancel request.");
    } finally {
      setActionId(null);
    }
  };

  const weekdays = moment.weekdaysShort(true);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="flex flex-col min-h-0 space-y-5 sm:space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] text-xs font-medium mb-3 backdrop-blur-sm">
            {t("calendar") || "Calendar"}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            {isAdmin
              ? t("adminCalendar") || "Organization calendar"
              : t("myCalendar") || "My calendar"}
          </h1>
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-xl">
            {isAdmin
              ? t("adminCalendarHint") ||
                "View events, approve time-off requests, and see who is unavailable."
              : t("memberCalendarHint") ||
                "View events and manage your time-off requests. Only you can see your pending requests."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            const today = moment().format("YYYY-MM-DD");
            setStartDate(today);
            setEndDate(today);
            setReason("");
            setRequestOpen(true);
          }}
          className={glassBtn}
        >
          <HiOutlinePlus className="w-4 h-4" />
          {t("requestTimeOff") || "Request time off"}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5">
        <section className="xl:col-span-2 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <button
              type="button"
              onClick={() =>
                setMonthCursor((m) => m.clone().subtract(1, "month"))
              }
              className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={t("previousMonth") || "Previous month"}
            >
              <HiOutlineChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100">
              {monthCursor.format("MMMM YYYY")}
            </h2>
            <button
              type="button"
              onClick={() => setMonthCursor((m) => m.clone().add(1, "month"))}
              className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={t("nextMonth") || "Next month"}
            >
              <HiOutlineChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekdays.map((d) => (
              <div
                key={d}
                className="text-center text-[11px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 py-1"
              >
                {d}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-7 gap-1 animate-pulse">
              {Array.from({ length: 35 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl bg-gray-100 dark:bg-gray-700/50"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day) => {
                const dateStr = day.format("YYYY-MM-DD");
                const inMonth = day.month() === monthCursor.month();
                const isToday = day.isSame(moment(), "day");
                const isSelected = dateStr === selectedDate;
                const dayReqs = requestsForDate(dateStr);
                const dayEvents = eventsForDate(dateStr);
                const hasApproved = dayReqs.some((r) => r.status === "approved");
                const hasPending = dayReqs.some((r) => r.status === "pending");
                const hasEvents = dayEvents.length > 0;

                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => selectDay(dateStr)}
                    className={`aspect-square min-h-[48px] sm:min-h-[56px] rounded-xl border p-1 sm:p-1.5 flex flex-col items-start transition-colors text-left ${
                      inMonth
                        ? "border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-900/25 hover:border-[#5932EA]/35"
                        : "border-transparent bg-transparent opacity-40"
                    } ${isToday ? "ring-2 ring-[#5932EA]/40" : ""} ${
                      isSelected ? "border-[#5932EA] bg-[#5932EA]/10 dark:bg-[#5932EA]/15" : ""
                    } ${
                      !isSelected && hasEvents
                        ? "bg-[#5932EA]/5 dark:bg-[#5932EA]/10"
                        : !isSelected && hasApproved
                          ? "bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200/70 dark:border-emerald-800/40"
                          : !isSelected && hasPending
                            ? "bg-amber-50/80 dark:bg-amber-900/20 border-amber-200/70 dark:border-amber-800/40"
                            : ""
                    }`}
                  >
                    <span
                      className={`text-xs sm:text-sm font-medium tabular-nums ${
                        inMonth
                          ? "text-gray-800 dark:text-gray-100"
                          : "text-gray-400"
                      }`}
                    >
                      {day.date()}
                    </span>
                    {(hasEvents || hasApproved || hasPending) && (
                      <span className="mt-auto flex flex-wrap gap-0.5">
                        {hasEvents && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#5932EA]" />
                        )}
                        {hasApproved && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        )}
                        {hasPending && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        )}
                      </span>
                    )}
                    {hasEvents && (
                      <span className="hidden sm:block w-full text-[10px] text-[#5932EA] truncate mt-0.5">
                        {dayEvents[0].title}
                        {dayEvents.length > 1 ? ` +${dayEvents.length - 1}` : ""}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#5932EA]" />
              {t("events") || "Events"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              {t("approved") || "Approved"} {t("timeOffShort") || "time off"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              {t("pending") || "Pending"} {t("timeOffShort") || "time off"}
            </span>
          </div>
        </section>

        <div className="space-y-4 sm:space-y-5">
          <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {moment(selectedDate).format("MMM D, YYYY")}
              </h3>
              <button
                type="button"
                onClick={() => openRequestForDay(selectedDate)}
                className={`${glassBtn} !py-1.5 !px-2.5 text-xs`}
              >
                <HiOutlinePlus className="w-3.5 h-3.5" />
                {t("timeOffShort") || "Time off"}
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
                  {t("events") || "Events"}
                </p>
                {selectedDayEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                    {t("noEventsOnDay") || "No events this day"}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {selectedDayEvents.map((event) => (
                      <li key={event.id}>
                        <Link
                          to={eventLink(event.id)}
                          className="group flex items-center gap-2.5 p-2.5 rounded-xl border border-gray-200/70 dark:border-gray-700/70 bg-white/50 dark:bg-gray-900/25 hover:border-[#5932EA]/30 hover:bg-[#5932EA]/5 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
                            <HiOutlineCalendarDays className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate group-hover:text-[#5932EA]">
                              {event.title}
                            </p>
                            {event.time && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {event.time}
                              </p>
                            )}
                          </div>
                          <HiOutlineArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#5932EA] shrink-0" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
                  {isAdmin
                    ? t("timeOffShort") || "Time off"
                    : t("myTimeOffShort") || "My time off"}
                </p>
                {selectedDayRequests.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-1">
                    {t("noTimeOffOnDay") || "No time off this day"}
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {selectedDayRequests.map((req) => (
                      <li
                        key={req.id}
                        className="flex items-center justify-between gap-2 text-sm px-2.5 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/30"
                      >
                        <span className="truncate text-gray-700 dark:text-gray-200">
                          {isAdmin
                            ? req.userName
                            : req.status === "pending"
                              ? t("yourPendingRequest") || "Your pending request"
                              : t("you") || "You"}
                        </span>
                        <span
                          className={`shrink-0 inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium border ${statusStyles[req.status]}`}
                        >
                          {t(req.status) || req.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
              {t("eventsThisMonth") || "Events this month"}
            </h3>
            {eventsInMonth.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                {t("noEventsThisMonth") || "No active events this month"}
              </p>
            ) : (
              <ul className="space-y-2 max-h-[220px] overflow-y-auto">
                {eventsInMonth.map((event) => (
                  <li key={event.id}>
                    <Link
                      to={eventLink(event.id)}
                      className="flex items-center gap-2.5 p-2.5 rounded-xl border border-gray-200/70 dark:border-gray-700/70 hover:border-[#5932EA]/30 hover:bg-[#5932EA]/5 transition-colors"
                    >
                      <div className="text-center shrink-0 w-10">
                        <p className="text-[10px] uppercase text-[#5932EA] font-semibold">
                          {moment(event.date).format("MMM")}
                        </p>
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-100 tabular-nums">
                          {moment(event.date).format("D")}
                        </p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {event.title}
                        </p>
                        {event.time && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {event.time}
                          </p>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {isAdmin ? (
            <section className="rounded-2xl border border-amber-200/70 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20 backdrop-blur-sm p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                {t("pendingTimeOff") || "Pending requests"}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {t("pendingTimeOffAdminHint") ||
                  "Only admins see everyone’s pending requests."}
              </p>
              {pendingForAdmin.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                  {t("noPendingTimeOff") || "No pending requests"}
                </p>
              ) : (
                <ul className="space-y-2.5 max-h-[320px] overflow-y-auto">
                  {pendingForAdmin.map((req) => (
                    <li
                      key={req.id}
                      className="p-3 rounded-xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-900/40"
                    >
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {req.userName || t("unknownUser")}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {req.startDate === req.endDate
                          ? req.startDate
                          : `${req.startDate} → ${req.endDate}`}
                      </p>
                      {req.reason && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {req.reason}
                        </p>
                      )}
                      <div className="flex gap-2 mt-2.5">
                        <button
                          type="button"
                          disabled={actionId === req.id}
                          onClick={() => reviewRequest(req, "approved")}
                          className={`${glassBtn} flex-1 !py-1.5 text-xs`}
                        >
                          <HiOutlineCheck className="w-3.5 h-3.5" />
                          {t("approve") || "Approve"}
                        </button>
                        <button
                          type="button"
                          disabled={actionId === req.id}
                          onClick={() => reviewRequest(req, "rejected")}
                          className={`${glassBtnMuted} flex-1 !py-1.5 text-xs`}
                        >
                          <HiOutlineXMark className="w-3.5 h-3.5" />
                          {t("reject") || "Reject"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : (
            myPending.length > 0 && (
              <section className="rounded-2xl border border-amber-200/70 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20 backdrop-blur-sm p-4 sm:p-5">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                  {t("myPendingTimeOff") || "My pending requests"}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {t("myPendingTimeOffHint") ||
                    "Only you and admins can see these until they’re approved."}
                </p>
                <ul className="space-y-2.5">
                  {myPending.map((req) => (
                    <li
                      key={req.id}
                      className="p-3 rounded-xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-900/40"
                    >
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {req.startDate === req.endDate
                          ? req.startDate
                          : `${req.startDate} → ${req.endDate}`}
                      </p>
                      {req.reason && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {req.reason}
                        </p>
                      )}
                      <button
                        type="button"
                        disabled={actionId === req.id}
                        onClick={() => cancelRequest(req)}
                        className="mt-2 text-xs font-medium text-gray-500 hover:text-red-500"
                      >
                        {t("cancelRequest") || "Cancel request"}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )
          )}

          <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
              {isAdmin
                ? t("approvedRejectedTimeOff") || "Approved & rejected"
                : t("myTimeOffRequests") || "My requests"}
            </h3>
            {listRequests.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                {t("noTimeOffRequests") || "No time-off requests yet"}
              </p>
            ) : (
              <ul className="space-y-2 max-h-[360px] overflow-y-auto">
                {listRequests.map((req) => (
                    <li
                      key={req.id}
                      className="p-3 rounded-xl border border-gray-200/70 dark:border-gray-700/70 bg-white/50 dark:bg-gray-900/25"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {isAdmin && (
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                              {req.userName}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {req.startDate === req.endDate
                              ? req.startDate
                              : `${req.startDate} → ${req.endDate}`}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium border ${statusStyles[req.status]}`}
                        >
                          {t(req.status) || req.status}
                        </span>
                      </div>
                      {req.reason && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {req.reason}
                        </p>
                      )}
                      {!isAdmin &&
                        req.userId === user?.uid &&
                        req.status === "pending" && (
                        <button
                          type="button"
                          disabled={actionId === req.id}
                          onClick={() => cancelRequest(req)}
                          className="mt-2 text-xs font-medium text-gray-500 hover:text-red-500"
                        >
                          {t("cancelRequest") || "Cancel request"}
                        </button>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <AnimatedModal
        isOpen={requestOpen}
        onClose={() => !submitting && setRequestOpen(false)}
        contentClassName="sm:max-w-md"
      >
        <div className={modalPanelClass}>
          <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
                <HiOutlineCalendarDays className="w-5 h-5" />
              </div>
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("requestTimeOff") || "Request time off"}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setRequestOpen(false)}
              className="p-2 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={t("close")}
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmitRequest} className="p-4 sm:p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  {t("startDate") || "Start date"}
                </label>
                <input
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (endDate && e.target.value > endDate) {
                      setEndDate(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  {t("endDate") || "End date"}
                </label>
                <input
                  type="date"
                  required
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                {t("reasonOptional") || "Reason (optional)"}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={300}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 resize-none"
                placeholder={t("timeOffReasonPlaceholder") || "e.g. Travel, family…"}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("timeOffPendingNote") ||
                "Your request stays pending until an admin approves it."}
            </p>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setRequestOpen(false)}
                className={`${glassBtnMuted} flex-1`}
                disabled={submitting}
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className={`${glassBtn} flex-1`}
                disabled={submitting}
              >
                {submitting
                  ? t("submitting") || "Submitting…"
                  : t("submitRequest") || "Submit request"}
              </button>
            </div>
          </form>
        </div>
      </AnimatedModal>
    </motion.div>
  );
};

export default CalendarPage;
